import { of, Observable, BehaviorSubject, throwError } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { isLocalhost } from 'src/main';
import { EventTracker } from '../../../assets/data/enum/eventTracker';
import { environment } from '../../../environments/environment';
import { GeneralUtilsService } from './common/generalUtils.service';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { SAASBRANDNAME } from '../../../assets/data/enum/saasBrandName.enum';
import { EventTrackerService } from './eventTracker.service';

interface AuthToken {
  username: string;
  roles: Array<string>;
  token_type: string;
  access_token: string;
  expires_in: number;
  refresh_token: string;
}

const KEYS_TO_KEEP_IN_LOCAL_STORAGE = [
  'iplkp',
  'configurationColumnTableStableLoads',
  'mobileBannerClosed',
];

@Injectable()
export class AuthenticationService {
  private cookieService = inject(CookieService);
  private loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // TODO: To remove once the memory leak is fixed
  private loginStatusSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private xRoleSale = 'xF_%';
  private xRolePepper = '%_Fx';
  private apiBaseUrl = environment.apiBaseUrl;
  private isAnalytics = ThemeConfig.brand === SAASBRANDNAME.ANALYTICS;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private permissionsService: NgxPermissionsService,
    private generalUtilsService: GeneralUtilsService,
    private eventTrackerService: EventTrackerService
  ) {}

  login(username: string, password: string): Observable<AuthToken> {
    if (!username || !password) {
      // Tratta il caso in cui username o password siano nulli o non definiti
      console.error('Nome utente o password mancanti');
      return throwError(() => new Error('Nome utente o password mancanti')); // Usa throwError per restituire un errore
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept-Language': this.translate.currentLang || '',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });

    const cleanUsername = username ? username.toLocaleLowerCase().trim() : null;

    return this.http
      .post<AuthToken>(
        this.apiBaseUrl + 'api/public/v1/auth/login',
        JSON.stringify({ username: cleanUsername, password }),
        { headers, withCredentials: true }
      )
      .pipe(
        switchMap((response: AuthToken) => {
          // Presumendo che AuthToken includa access_token e refresh_token
          if (response && response.access_token && response.refresh_token) {
            this.setAuthToken(response);
            this.setPermissionByToken();
            this.setLoggedCompanyData();
            this.eventTrackerService.trackEvent(EventTracker.LOGIN, null);
            return of(response);
          } else {
            return throwError(() => new Error('Risposta di login non valida'));
          }
        }),
        catchError((error: any) => {
          console.error('Errore di login:', error);
          return throwError(() => error); // Rilancia l'errore
        })
      );
  }

  // eslint-disable-next-line
  loginViaOauth(token): Observable<any> {
    if (token) {
      return this.storeToken({ access_token: token });
    }
  }

  private storeToken(token): Observable<any> {
    const observable = new Observable((observer) => {
      this.setAuthToken(token);
      this.setPermissionByToken();
      this.setLoggedCompanyData();
      observer.next(true);
      observer.complete();
    });
    return observable;
  }

  get isLoggedIn() {
    return this.loggedIn.asObservable();
  }

  // TODO: To remove once the memory leak is fixed Added to avoid the subscription loop given by isLoggedIn method
  loginStatus() {
    return this.loginStatusSubject.asObservable();
  }

  isAuthTokenValide() {
    if (this.getAuthToken() && this.getAuthToken().access_token) {
      if (!this.isAuthTokenExpired()) {
        //* Analytics V1 and V2 navigation is handled via cookies
        // If user login in V2 and navigates to V1
        // there is the risk that localstorage is not set correctly
        // so we reset localstorage with token info everytime we check token validity
        if (this.isAnalytics) {
          this.resetLocalStorageWithToken();
        }
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  private resetLocalStorageWithToken() {
    const token = this.getAuthToken();
    this.setAuthToken(token);
    this.setPermissionByToken();
    this.setLoggedCompanyData();
  }

  // Initial check to check login status on app reloading
  setLoginStatus() {
    // checking login status on initial load
    if (localStorage.getItem('auth_tokens')) {
      if (!this.isAuthTokenExpired()) {
        this.loginStatusSubject.next(true);
      }
    }
  }

  isAuthTokenExpired() {
    const authToken = this.getAuthToken()?.access_token;
    if (authToken) {
      return this.generalUtilsService.isTokenExpired(authToken);
    }
    return true;
  }

  parseJwt(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  }

  setAuthToken = (authTokens: AuthToken) => {
    //* Analytics V1 and V2 navigation is handled via cookies
    // Set cookie only for Analytics brand
    if (this.isAnalytics) {
      this.cookieService.set('analytics_auth_tokens', JSON.stringify(authTokens), {
        expires: authTokens.expires_in / 86400, // Convert seconds to days
        path: '/',
        domain: isLocalhost ? 'localhost' : '.xfarm.ag',
        secure: true,
      });
    }
    localStorage.setItem('auth_tokens', JSON.stringify(authTokens));
  };

  setLoggedCompanyData = () => {
    const b64dec = this.getJwtToken();
    if (b64dec && b64dec.farmId) {
      localStorage.setItem('companyLoggedIn', JSON.stringify(b64dec.farmId));
      localStorage.setItem('companySelected', JSON.stringify({ id: b64dec.farmId }));
    }
  };

  getAuthToken = (): AuthToken => {
    //* Analytics V1 and V2 navigation is handled via cookies
    const authToken = this.isAnalytics
      ? this.cookieService.get('analytics_auth_tokens')
      : localStorage.getItem('auth_tokens');
    if (!authToken) {
      return null;
    }
    return JSON.parse(authToken);
  };

  // Returns token stored in localStorage
  getJwtToken = () => {
    if (this.getAuthToken() && this.getAuthToken().access_token) {
      return this.parseJwt(this.getAuthToken().access_token);
    } else {
      return null;
    }
  };

  // Sets the user login status
  setPermissionByToken = () => {
    // Cancella tutti i permessi correnti
    this.permissionsService.flushPermissions();

    // Ottieni il token JWT (presumo che tu abbia già un metodo getJwtToken)
    const b64dec = this.getJwtToken();

    // Verifica se il token è valido e contiene ruoli
    if (b64dec && b64dec.roles && b64dec.roles.length > 0) {
      // Se l'utente ha un solo ruolo, aggiungilo
      if (b64dec.roles.length === 1) {
        const xRole = this.xRoleSale + b64dec.roles[0].toLowerCase() + this.xRolePepper;
        this.permissionsService.addPermission(xRole);
      } else if (b64dec.roles.length > 1) {
        // Se l'utente ha più di un ruolo, aggiungi ciascun ruolo
        b64dec.roles.forEach((role) => {
          const xRole = this.xRoleSale + role.toLowerCase() + this.xRolePepper;
          this.permissionsService.addPermission(xRole);
        });
      }
      this.loggedIn.next(true);
      this.loginStatusSubject.next(true);
    }
  };

  removeCookies() {
    const cookiesDomain = isLocalhost ? 'localhost' : '.xfarm.ag';
    this.cookieService.deleteAll('/', cookiesDomain);
  }

  logout(enableReload = true) {
    this.handleLocalStorage();

    this.permissionsService.flushPermissions();
    this.removeCookies();
    this.loggedIn.next(false);
    this.loginStatusSubject.next(false);

    if (enableReload === true) {
      this.eventTrackerService.trackEvent(EventTracker.LOGOUT, null);
      this.eventTrackerService.reset();

      if (!localStorage.getItem('reloadCheck')) {
        localStorage.setItem('reloadCheck', 'true');
        location.reload();
      }
    }
  }

  logoutTafe() {
    this.handleLocalStorage();

    localStorage.clear();
    this.eventTrackerService.reset();

    this.permissionsService.flushPermissions();
    this.removeCookies();
    this.loggedIn.next(false);
    this.loginStatusSubject.next(false);

    const redirectUriFinal = 'oauth/callback/TafeOAuth20Client?client_name=TafeOAuth20Client';
    const redirectUri = encodeURIComponent(environment.apiBaseUrl + redirectUriFinal);

    let keycloakLogoutUrl;

    if (ThemeConfig.brand === SAASBRANDNAME.TAFEDIGIAG) {
      keycloakLogoutUrl = `https://auth-tafe.xfarm.ag/realms/TAFE/protocol/openid-connect/logout?redirect_uri=${redirectUri}`;
    } else if (ThemeConfig.brand === SAASBRANDNAME.TAFEDIGIAGDEV) {
      keycloakLogoutUrl = `https://auth-tafe-dev.xfarm.ag/realms/TAFE/protocol/openid-connect/logout?redirect_uri=${redirectUri}`;
    }

    window.location.href = keycloakLogoutUrl;
  }

  logoutFieldbee() {
    this.handleLocalStorage();

    localStorage.clear();
    this.eventTrackerService.reset();

    this.permissionsService.flushPermissions();
    this.removeCookies();
    this.loggedIn.next(false);
    this.loginStatusSubject.next(false);

    const redirectUriFinal =
      'oauth/callback/FieldBeeOAuth20Client?client_name=FieldBeeOAuth20Client';
    const redirectUri = encodeURIComponent(environment.apiBaseUrl + redirectUriFinal);

    let keycloakLogoutUrl = '';

    if (ThemeConfig.brand === SAASBRANDNAME.FIELDBEE) {
      // PROD
      keycloakLogoutUrl = `https://auth.fieldbee.com/realms/fieldbee/protocol/openid-connect/logout?redirect_uri=${redirectUri}`;
    } else if (ThemeConfig.brand === SAASBRANDNAME.FIELDBEEDEV) {
      // DEV
      keycloakLogoutUrl = `https://auth.fieldbee.com/realms/dev/protocol/openid-connect/logout?redirect_uri=${redirectUri}`;
    }

    window.location.href = keycloakLogoutUrl;
  }

  softLogout() {
    this.handleLocalStorage();
  }

  /**
   *
   * @description This function manages localStorage during logout, using an array to preserve specific keys and values after the user logs out.
   */
  handleLocalStorage() {
    if (!KEYS_TO_KEEP_IN_LOCAL_STORAGE.length) {
      return;
    }

    const fromLocalStorage = KEYS_TO_KEEP_IN_LOCAL_STORAGE.map((key) => ({
      key,
      value: localStorage.getItem(key),
    }));

    localStorage.clear();

    fromLocalStorage.forEach(({ key, value }) => {
      if (key && value && value !== 'null') {
        localStorage.setItem(key, value);
      }
    });
  }
}
